import { Fragment, useState } from 'react';
import styled from 'styled-components';
import { Box } from '@99designs/emu';
import { CarouselItems, CarouselSteps, Ribbon, RibbonSize } from '@99designs/marketing-ui';
import { linkTo } from '@99designs/routing';
import { ProductGroupType } from '../../types';
import { ContestCustomerAttribution, CustomerAttribution } from './CustomerAttribution';
import { ContestDesignerAttribution, DesignerAttribution } from './DesignerAttribution';
import type { Image } from '../../types';

interface Props {
    images: Image[];
    type?: ProductGroupType;
}

const Container = styled.div`
    position: relative;
    text-align: right;
`;

const ImageOverlay = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(
        180deg,
        #faf9f7 0%,
        rgba(250, 249, 247, 0) 25%,
        rgba(250, 249, 247, 0) 75%,
        #faf9f7 100%
    );
    mix-blend-mode: multiply;
`;

export function Hero({ images, type = ProductGroupType.Contest }: Props) {
    const [currentStep, setCurrentStep] = useState(0);
    const { customerAttribution, designerAttribution } = images[currentStep] || {};
    const contentfulImageDomain = 'images.ctfassets.net';

    return (
        <Container>
            <CarouselItems
                items={images.map(({ src, alt }, index) => (
                    <Fragment key={src}>
                        <ImageOverlay />
                        {src.includes(contentfulImageDomain) ? (
                            <picture>
                                <source srcSet={`${src}?fm=avif&q=80`} />
                                <img src={`${src}?fm=jpg&fl=progressive&q=80`} alt={alt} />
                            </picture>
                        ) : (
                            <img src={`${src}?auto=format&w=606&q=45&dpr=2`} alt={alt} />
                        )}
                        {type === ProductGroupType.Contest && index === 0 && (
                            <Box position="absolute" top="1bu" right="0.75bu">
                                <Ribbon size={RibbonSize.Small}>{__('Winner')}</Ribbon>
                            </Box>
                        )}
                    </Fragment>
                ))}
                currentItemIndex={currentStep}
                onSwipe={setCurrentStep}
            />
            <Box
                display="flex"
                alignItems="center"
                marginTop="1bu"
                justifyContent={images.length > 1 ? 'space-between' : 'end'}
            >
                {images.length > 1 && (
                    <CarouselSteps
                        count={images.length}
                        currentIndex={currentStep}
                        onClick={setCurrentStep}
                    />
                )}
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="end"
                    marginLeft={images.length > 1 ? '1bu' : 'none'}
                >
                    {customerAttribution?.text &&
                        (type === ProductGroupType.Contest ? (
                            <ContestCustomerAttribution
                                link={customerAttribution.link}
                                displayName={customerAttribution.text}
                            />
                        ) : (
                            <CustomerAttribution
                                link={customerAttribution.link}
                                displayName={customerAttribution.text}
                            />
                        ))}
                    {designerAttribution?.text &&
                        (type === ProductGroupType.Contest ? (
                            <ContestDesignerAttribution
                                displayName={designerAttribution.text}
                                link={
                                    designerAttribution.userId &&
                                    linkTo.userProfile(designerAttribution.userId)
                                }
                            />
                        ) : (
                            <DesignerAttribution
                                displayName={designerAttribution.text}
                                link={
                                    designerAttribution.userId &&
                                    linkTo.userProfile(designerAttribution.userId)
                                }
                            />
                        ))}
                </Box>
            </Box>
        </Container>
    );
}
